import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { UserPlus } from 'lucide-react'

import { UserModal } from '../UserProfile/Modal'

import type { FeedItem as FeedItemType } from '@/features/profiles/types/feed'
import {
  isFollowFeedItem,
  isNotificationFeedItem,
  isProgressFeedItem
} from '@/features/profiles/types/feed'
import { SocialFeedActionBlock } from '@/features/social/components/SocialFeedActionBlock'
import { useModalContext } from '@/shared/contexts/Modal'
import { useIsCurrentViewer } from '@/shared/hooks/useCurrentUserUid'
import { useUsersAnalytics } from '@/shared/lib/analytics'
import { urlsFromUserCosmetics } from '@/shared/lib/user-feed-avatar-urls'
import { AvatarComposite } from '@/shared/ui/AvatarComposite'
import { Badge } from '@/shared/ui/badge'
import {
  FeedActivityAccentWell,
  FeedCardSurface
} from '@/shared/ui/FeedCardSurface'

import { NotificationFeedItem } from './NotificationFeedItem'

interface FeedItemProps {
  item: FeedItemType
  showActions?: boolean
}

function getNamedResourceTitle(resource: unknown) {
  if (typeof resource !== 'object' || resource === null) return null
  if (!('name' in resource)) return null
  const name = (resource as { name?: unknown }).name
  return typeof name === 'string' ? name : null
}

function RelationshipBadge({
  itsMe,
  isFollowing
}: {
  itsMe: boolean
  isFollowing: boolean
}) {
  if (itsMe) return null
  if (isFollowing) {
    return (
      <Badge className="border-0 bg-white/10 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide text-white/90">
        Seguindo
      </Badge>
    )
  }
  return (
    <Badge className="border-0 bg-primary-dark px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide text-white shadow-[0_1px_0_0_rgba(0,0,0,0.35)]">
      Seguir
    </Badge>
  )
}

const FeedItemBody = ({ item, showActions = true }: FeedItemProps) => {
  const resourceType = isProgressFeedItem(item)
    ? item.resource.resource_type
    : null
  const isLevel = resourceType === 'Level'
  const isMission = resourceType === 'Mission'
  const isFollow = isFollowFeedItem(item)
  const { openModal } = useModalContext()
  const itsMe = useIsCurrentViewer(item.from.uid)
  const { trackUserFeedInteraction } = useUsersAnalytics()
  const avatarUrls = urlsFromUserCosmetics(item.from)

  const handleOpenProfile = () => {
    trackUserFeedInteraction('clicked', item.from.uid)

    openModal(
      {
        modal: UserModal
      },
      {
        userUid: item.from.uid
      }
    )
  }

  const title =
    isProgressFeedItem(item) && (isLevel || isMission)
      ? (getNamedResourceTitle(item.resource.resource) ?? '')
      : ''

  const accent = isLevel ? (
    <img
      src="/icons/throphy.svg"
      alt=""
      className="size-6 drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)]"
    />
  ) : isMission ? (
    <img
      src="/icons/missions.svg"
      alt=""
      className="size-5 opacity-95 drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)]"
    />
  ) : isFollow ? (
    <UserPlus
      className="size-5 text-[#9ff0bd]"
      strokeWidth={2.25}
      aria-hidden
    />
  ) : null

  return (
    <FeedCardSurface density="compact" onClick={handleOpenProfile}>
      <div className="flex gap-2">
        <div className="pointer-events-none shrink-0">
          <AvatarComposite {...avatarUrls} size="xs" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold leading-tight tracking-tight text-white">
                {itsMe ? 'Você' : item.from.name}
              </p>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                <time
                  className="text-[11px] font-medium leading-tight text-[#7F7FAD]"
                  dateTime={item.created_at}
                >
                  {formatDistanceToNow(new Date(item.created_at), {
                    addSuffix: true,
                    locale: ptBR
                  })}
                </time>
                <RelationshipBadge
                  itsMe={itsMe}
                  isFollowing={item.from.is_following}
                />
              </div>
            </div>
            {accent ? (
              <FeedActivityAccentWell size="sm" className="rounded-md">
                {accent}
              </FeedActivityAccentWell>
            ) : null}
          </div>
          <div className="pt-1.5">
            {isLevel && (
              <p className="text-[13px] leading-snug text-[#c8c8dc]">
                <span className="font-medium text-white/90">Concluiu o nível</span>{' '}
                <span className="font-semibold text-primary-foreground">{title}</span>
              </p>
            )}
            {isMission && (
              <p className="text-[13px] leading-snug text-[#c8c8dc]">
                <span className="font-medium text-white/90">Concluiu a missão</span>{' '}
                <span className="font-semibold text-primary-foreground">{title}</span>
              </p>
            )}
            {isFollow && !isLevel && !isMission && (
              <p className="text-[13px] font-semibold leading-snug text-white">
                Começou a seguir você
              </p>
            )}
          </div>
        </div>
      </div>
      {showActions && <SocialFeedActionBlock item={item} compact />}
    </FeedCardSurface>
  )
}

export const FeedItem = ({ item, showActions = true }: FeedItemProps) => {
  if (isNotificationFeedItem(item)) {
    return (
      <NotificationFeedItem
        item={item}
        resource={item.resource}
        showActions={showActions}
      />
    )
  }

  return <FeedItemBody item={item} showActions={showActions} />
}
